// src/pages/ArticlePage.jsx
import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  ArrowRight,
  Facebook,
  Link2,
  Linkedin,
  Twitter,
} from "lucide-react";
import PublicLayout from "../components/layout/PublicLayout";
import { getArticleBySlug, getAllArticles } from "../services/articleService";
import {
  DEFAULT_AUTHOR,
  DEFAULT_IMAGE,
  DEFAULT_KEYWORDS,
  SITE_NAME,
  absoluteUrl,
  createArticleSchema,
  createBreadcrumbSchema,
  truncate,
} from "../utils/seo";

const withTimeout = (promise, ms = 4500) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Article load timed out")), ms)
    ),
  ]);

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

const formatShortDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const ArticlePage = ({ isDownloading, handleDownload }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const shareUrl = absoluteUrl(`/article/${slug}`);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    root.style.scrollBehavior = previousScrollBehavior;
  }, [slug]);

  const loadArticle = useCallback(async () => {
    try {
      setLoading(true);
      const [articleData, allArticles] = await withTimeout(
        Promise.all([getArticleBySlug(slug), getAllArticles()])
      );
      setArticle(articleData);

      if (articleData) {
        const related = allArticles
          .filter((item) => item.slug !== slug && item.visibility === "public")
          .filter(
            (item) =>
              item.category === articleData.category ||
              item.tags?.some((tag) => articleData.tags?.includes(tag))
          )
          .slice(0, 4);

        const fill = allArticles
          .filter(
            (item) =>
              item.slug !== slug &&
              item.visibility === "public" &&
              !related.find((entry) => entry.slug === item.slug)
          )
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 4 - related.length);

        setRelatedArticles([...related, ...fill]);
      }
    } catch (error) {
      console.error("Error loading article:", error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  const handleCopyLink = async () => {
    const flashCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    };

    try {
      await navigator.clipboard.writeText(shareUrl);
      flashCopied();
    } catch {
      try {
        const helper = document.createElement("textarea");
        helper.value = shareUrl;
        helper.setAttribute("readonly", "");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        document.body.removeChild(helper);
        flashCopied();
      } catch {
        window.prompt("Copy link, then close:", shareUrl);
      }
    }
  };

  const shareLinks = [
    {
      label: "Share on X",
      icon: <Twitter size={15} />,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl
      )}&text=${encodeURIComponent(article?.title || "")}`,
    },
    {
      label: "Share on Facebook",
      icon: <Facebook size={15} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl
      )}`,
    },
    {
      label: "Share on LinkedIn",
      icon: <Linkedin size={15} />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl
      )}`,
    },
  ];

  const articleTitle = article
    ? `${article.title} | ${DEFAULT_AUTHOR}`
    : `Article not found | ${SITE_NAME}`;
  const articleDescription = article
    ? truncate(article.excerpt || article.description, 155)
    : "This article could not be found in Aurio Rajaa's portfolio.";
  const articleImage = absoluteUrl(article?.image || DEFAULT_IMAGE);
  const articleKeywords = article
    ? [...new Set([...(article.tags || []), ...DEFAULT_KEYWORDS])].join(", ")
    : DEFAULT_KEYWORDS.join(", ");

  if (loading) {
    return (
      <PublicLayout
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      >
        <div className="public-loading">
          <div className="progress" aria-hidden="true">
            <div className="indeterminate" />
          </div>
          <p>Fetching the story…</p>
        </div>
      </PublicLayout>
    );
  }

  if (!article) {
    return (
      <PublicLayout
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      >
        <Helmet>
          <title>{articleTitle}</title>
          <link rel="canonical" href={shareUrl} />
          <meta name="description" content={articleDescription} />
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <div className="container detail-page">
          <button
            type="button"
            className="detail-back"
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={15} />
            Back to portfolio
          </button>
          <div className="project-empty">
            <h3>Article not found</h3>
            <p>This piece may have moved or been unpublished.</p>
            <button
              type="button"
              className="btn"
              style={{ marginTop: "1rem" }}
              onClick={() => navigate("/")}
            >
              Back to portfolio
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const articleSchema = createArticleSchema(article, shareUrl);
  const breadcrumbs = createBreadcrumbSchema([
    { name: "Home", item: "/" },
    { name: "Articles", item: "/#articles" },
    { name: article.title, item: `/article/${article.slug || slug}` },
  ]);

  return (
    <PublicLayout
      isDownloading={isDownloading}
      handleDownload={handleDownload}
    >
      <Helmet>
        <title>{articleTitle}</title>
        <link rel="canonical" href={shareUrl} />
        <meta name="description" content={articleDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={articleTitle} />
        <meta property="og:description" content={articleDescription} />
        <meta property="og:image" content={articleImage} />
        <meta property="og:image:secure_url" content={articleImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={`${article.title} article cover`}
        />
        <meta
          property="article:author"
          content={article.author || DEFAULT_AUTHOR}
        />
        <meta property="article:published_time" content={article.date} />
        {article.updatedAt && (
          <meta property="article:modified_time" content={article.updatedAt} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={articleTitle} />
        <meta name="twitter:description" content={articleDescription} />
        <meta name="twitter:image" content={articleImage} />
        <meta
          name="twitter:image:alt"
          content={`${article.title} article cover`}
        />
        <meta name="twitter:site" content="@auriorajaa" />
        <meta name="twitter:creator" content="@auriorajaa" />
        <meta name="author" content={article.author || DEFAULT_AUTHOR} />
        <meta name="keywords" content={articleKeywords} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbs)}
        </script>
      </Helmet>

      <div className="container detail-page">
        <button
          type="button"
          className="detail-back"
          onClick={() =>
            navigate("/", { state: { scrollTo: "articles" } })
          }
        >
          <ArrowLeft size={15} />
          All writing
        </button>

        <header className="detail-header">
          <p className="public-eyebrow">Writing</p>
          <h1 className="detail-title">{article.title}</h1>
          {article.excerpt && (
            <p className="detail-description">{article.excerpt}</p>
          )}

          <div className="detail-byline">
            <img
              className="detail-avatar"
              src="/profilepic.png"
              alt={article.author || DEFAULT_AUTHOR}
            />
            <div>
              <strong>{article.author || DEFAULT_AUTHOR}</strong>
              <span>
                {[formatDate(article.date), article.readTime]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </div>

          {(article.categoryLabel || article.featured) && (
            <div className="detail-tags">
              {article.categoryLabel && (
                <span className="public-chip public-chip--primary">
                  {article.categoryLabel}
                </span>
              )}
              {article.featured && (
                <span className="public-chip public-chip--accent">
                  Featured
                </span>
              )}
            </div>
          )}
        </header>

        {article.image && (
          <figure className="article-cover">
            <img
              src={article.image}
              alt={article.title}
              loading="lazy"
              decoding="async"
            />
          </figure>
        )}

        <div className="article-layout">
          <article
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.description || "" }}
          />

          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h2 className="sidebar-title">Share this article</h2>
              <div className="share-list">
                {shareLinks.map((share) => (
                  <a
                    className="share-link"
                    key={share.label}
                    href={share.href}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {share.icon}
                    {share.label}
                  </a>
                ))}
                <button
                  type="button"
                  className={`copy-link-button${copied ? " is-copied" : ""}`}
                  onClick={handleCopyLink}
                >
                  <Link2 size={15} />
                  {copied ? "Link copied" : "Copy link"}
                </button>
              </div>
            </div>

            {article.tags?.length > 0 && (
              <div className="sidebar-card">
                <h2 className="sidebar-title">Topics</h2>
                <div className="chip-row">
                  {article.tags.map((tag) => (
                    <span className="public-chip" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <div className="sidebar-card">
                <h2 className="sidebar-title">Keep reading</h2>
                <div className="related-list">
                  {relatedArticles.map((related) => (
                    <button
                      type="button"
                      className="related-item"
                      key={related.id || related.slug}
                      onClick={() => navigate(`/article/${related.slug}`)}
                    >
                      <span className="related-title">{related.title}</span>
                      <span className="related-date">
                        {formatShortDate(related.date)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        <div className="article-end">
          <p>That&apos;s a wrap. Thanks for reading.</p>
          <button
            type="button"
            className="btn btn-outline"
            style={{ marginTop: "0.9rem" }}
            onClick={() =>
              navigate("/", { state: { scrollTo: "articles" } })
            }
          >
            Browse more writing
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ArticlePage;
